import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { NAV } from '@/constants/testIds';

const NAV_ITEMS = [
  { label: 'Why Valanti', to: '/why-valanti-labs', key: 'why' },
  { label: 'How It Works', to: '/how-it-works', key: 'how' },
  { label: 'Technology', to: '/technology', key: 'tech' },
  { label: 'Who It Helps', to: '/for-individuals', key: 'who' },
  { label: 'Research', to: '/research', key: 'research' },
  { label: 'Partners', to: '/partners', key: 'partners' },
  { label: 'Support', to: '/support', key: 'support' },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? 'bg-vl-bg/85 backdrop-blur-md border-b hairline' : 'bg-transparent'
      }`}
    >
      <div className="vl-container flex items-center justify-between h-16 lg:h-20">
        <Link
          to="/"
          data-testid={NAV.brand}
          className="flex items-center gap-2.5 group"
          aria-label="Valanti Labs home"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-vl-ink text-vl-bg font-serif-display text-lg group-hover:bg-vl-violet transition-colors">
            V
          </span>
          <span className="font-serif-display text-xl text-vl-ink tracking-tight">Valanti Labs</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              data-testid={NAV.link(item.key)}
              className={({ isActive }) =>
                `text-[14px] font-medium tracking-wide transition-colors ${
                  isActive ? 'text-vl-ink' : 'text-vl-ink/65 hover:text-vl-ink'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/early-access"
            data-testid={NAV.ctaEarly}
            className="inline-flex items-center justify-center rounded-full bg-vl-ink text-vl-bg px-5 py-2.5 text-sm font-medium hover:bg-vl-violet transition-colors"
          >
            Join Early Access
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Open navigation menu"
          data-testid={NAV.menuToggle}
          className="lg:hidden p-2 -mr-2 text-vl-ink"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid={NAV.mobilePanel} className="lg:hidden border-t hairline bg-vl-bg">
          <div className="vl-container py-4 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                data-testid={NAV.link(`${item.key}-mobile`)}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b hairline ${
                    isActive ? 'text-vl-ink' : 'text-vl-ink/70'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/early-access"
              data-testid={NAV.ctaEarly + '-mobile'}
              className="mt-4 inline-flex items-center justify-center rounded-full bg-vl-ink text-vl-bg px-5 py-3 text-sm font-medium"
            >
              Join Early Access
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
